import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  effect,
  signal,
  computed,
  PLATFORM_ID,
} from '@angular/core';
import * as echarts from 'echarts';
import { IEnergyTip, LISTA_AHORRO } from '../utils/lista-ahorro.data';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { LazyLoadEvent } from 'primeng/api'; // Intenta con este primero
import { IconFieldModule } from 'primeng/iconfield';
import { Tooltip } from 'primeng/tooltip';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { BillingService } from '../_core/billings';
import { catchError, finalize, of, tap } from 'rxjs';
import { runInInjectionContext, Injector } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { ISettlementItem } from '../core.interface';
import { Core } from '../_core/core';
import { HomeS } from '../_core/home';
import { BrandingService } from '../_core/branding.service';
import { Validators } from '@angular/forms';
import { Spinner } from '../_core/spinner';
import { ToastService } from '../_core/toast.service';

import { forkJoin, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
@Component({
  selector: 'app-billings',
  imports: [
    CommonModule,
    TooltipModule,
    DatePickerModule,
    SelectModule,
    ReactiveFormsModule,
    LucideAngularModule,
    TableModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    /* InputGroupAddonModule, */ InputTextModule,
    PopoverModule,
    ButtonModule,
    SkeletonModule,
    FormsModule,
    MessageModule,
  ],

  templateUrl: './billings.html',
  styleUrl: './billings.scss',
})
export class Billings implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private billingService = inject(BillingService);
  private $spinner = inject(Spinner);
  private $toast = inject(ToastService);
  private $core = inject(Core);
  private router = inject(Router);
  private $home = inject(HomeS);
  private $branding = inject(BrandingService);
  private injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);

  public isDownloading = signal(false);
  public readonly isNarrowViewport = signal(false);
  private narrowViewportQuery: MediaQueryList | null = null;
  private narrowViewportListener: ((event: MediaQueryListEvent) => void) | null = null;
  public homeData = this.$home.homeData;
  public readonly isGGM = this.$branding.isGGM;

  public settlements: ISettlementItem[] = [];
  public settlementsSelected: ISettlementItem[] = [];
  public predefinedDateOptions = [
    {
      label: 'Últimos 30 días',
      value: {
        startDate: this.getDateDaysAgo(30),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 60 días',
      value: {
        startDate: this.getDateMonthsAgo(2),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 6 meses',
      value: {
        startDate: this.getDateMonthsAgo(6),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 12 meses',
      value: {
        startDate: this.getDateYearsAgo(1),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 24 meses',
      value: {
        startDate: this.getDateYearsAgo(2),
        endDate: this.formatDate(new Date()),
      },
    },
  ];
  public chartSignal = this.billingService.chartSettlementsByDate;
  public settlementsSignal = this.billingService.paginatedSettlements;
  public totalRecords = this.billingService.totalElements;
  public useFixedTableRowHeight = computed(
    () => this.settlementsSignal().length > 0 && this.settlementsSignal().length < 8,
  );
  public lastSettlement = this.billingService.lastSettlement;

  public loading = signal(false);
  public chartLoading = signal(false);
  public tableInitialized = signal(false);
  private initialTableLoad = true;
  public showCalendar: boolean = false;
  public showDateRangeYearWarning = signal(false);

  public first: number = 0;
  public rows: number = 10;

  public maxDate: Date = new Date();
  public minDate: Date = new Date();
  public supplies: Array<{ supplyName: string; value: string }> = [];
  public configForm!: FormGroup;

  private chartResizeHandler: (() => void) | null = null;
  private chartInstance: echarts.ECharts | null = null;

  get comparativas() {
    return this.configForm.get('comparativas') as FormArray;
  }
  constructor(private fb: FormBuilder) {}
  async ngOnInit() {
    this.initNarrowViewportListener();
    this.initForms();

    const hoy = new Date();
    this.maxDate = hoy;
    const hace5Anios = new Date();
    hace5Anios.setFullYear(hoy.getFullYear() - 5);
    this.minDate = hace5Anios;

    let tenantSelected = this.$core.selectedBuilding();
    if (!tenantSelected) {
      tenantSelected = await this.$core.getTenantFromLocal();
    }

    const dateRange = this.getSelectedDateRange();
    if (!dateRange || !tenantSelected?.tenantId) {
      return;
    }

    const chartPrefetched = this.isChartPrefetched(
      tenantSelected.tenantId,
      dateRange.dateStart,
      dateRange.dateEnd,
    );
    const tablePrefetched = this.isTablePrefetched(
      tenantSelected.tenantId,
      dateRange.dateStart,
      dateRange.dateEnd,
    );

    this.initialTableLoad = true;
    this.first = 0;
    this.tableInitialized.set(tablePrefetched);

    if (!chartPrefetched) {
      this.loading.set(true);
      this.$spinner.show();
    }

    if (!this.homeData()) {
      await this.$home.fetchHomeData(true);
    }

    if (!chartPrefetched) {
      await this.billingService.fetchSettlementsForChart(
        tenantSelected.tenantId,
        dateRange.dateStart,
        dateRange.dateEnd,
        { manageSpinner: false },
      );
    }

    this.syncSupplySelection();

    if (!chartPrefetched) {
      this.$spinner.hide();
    }
    queueMicrotask(() => this.loading.set(false));
  }

  ngOnDestroy(): void {
    this.disposeChart();
    if (this.narrowViewportQuery && this.narrowViewportListener) {
      this.narrowViewportQuery.removeEventListener('change', this.narrowViewportListener);
    }
  }

  get settlementsTableScrollable(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableScrollHeight(): string | undefined {
    if (this.isNarrowViewport() || this.useFixedTableRowHeight()) {
      return undefined;
    }
    return 'calc(-300px + 95vh)';
  }

  get settlementsTableStyle(): Record<string, string> {
    if (this.isNarrowViewport()) {
      return { width: '100%' };
    }
    if (this.useFixedTableRowHeight()) {
      return { 'min-width': '50rem' };
    }
    return { 'min-width': '50rem', 'min-height': 'calc(-400px + 95vh)' };
  }

  get settlementsTableShowCurrentPageReport(): boolean {
    return true;
  }

  get settlementsTableShowPageLinks(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableShowFirstLastIcon(): boolean {
    return !this.isNarrowViewport();
  }

  get settlementsTableRowsPerPageOptions(): number[] | undefined {
    return this.isNarrowViewport() ? undefined : [10, 20, 50, 100];
  }

  get settlementsTablePaginatorStyleClass(): string | undefined {
    if (!this.isNarrowViewport()) {
      return undefined;
    }
    return 'w-100 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2';
  }

  /** Sincroniza lista de suministros, autoselección y filtro del chart. */
  private syncSupplySelection(): void {
    const typesFromChart = this.billingService.getGroupedSupplyTypes();
    const supplyControl = this.configForm.controls['supplyType'];

    // Respuesta sin liquidaciones: no borrar opciones ni valor ya seleccionado
    if (typesFromChart.length === 0 && this.supplies.length > 0) {
      const current = supplyControl.value as string | null;
      if (current != null) {
        if (this.supplies.length === 1) {
          supplyControl.disable({ emitEvent: false });
        } else {
          supplyControl.enable({ emitEvent: false });
        }
        this.billingService.filterChartBySupplyType(current);
      }
      return;
    }

    if (typesFromChart.length === 0) {
      supplyControl.enable({ emitEvent: false });
      this.supplies = [];
      this.configForm.patchValue({ supplyType: null }, { emitEvent: false });
      this.billingService.filterChartBySupplyType(null);
      return;
    }

    this.supplies = typesFromChart.map((x) => ({
      supplyName: x,
      value: x,
    }));

    const current = supplyControl.value as string | null;
    const stillValid = current != null && this.supplies.some((s) => s.value === current);
    const selected = stillValid ? current : this.supplies[0].value;

    supplyControl.enable({ emitEvent: false });
    this.configForm.patchValue({ supplyType: selected }, { emitEvent: false });

    if (this.supplies.length === 1) {
      supplyControl.disable({ emitEvent: false });
    }

    this.billingService.filterChartBySupplyType(selected);
  }

  /** Tabla precargada en página 0 para el tenant y rango actuales. */
  private isTablePrefetched(tenantId: string, from: string, to: string): boolean {
    return this.billingService.isReusableTableCache(tenantId, from, to);
  }

  /** Gráfica ya cargada para el tenant y rango actuales. */
  private isChartPrefetched(tenantId: string, from: string, to: string): boolean {
    return this.billingService.isChartForTenant(tenantId, from, to);
  }

  private getSelectedDateRange(): { dateStart: string; dateEnd: string } | null {
    if (!this.configForm) return null;

    if (this.showCalendar) {
      const { inicio, fin } = this.comparativas.at(0).value;
      const dateStart = this.formatDateValue(inicio);
      const dateEnd = this.formatDateValue(fin);
      if (!dateStart || !dateEnd) return null;
      return { dateStart, dateEnd };
    }

    const range = this.configForm.get('predefineDateOption')?.value?.value;
    if (!range) return null;

    return {
      dateStart: range.startDate,
      dateEnd: range.endDate,
    };
  }

  private formatDateValue(date: unknown): string | null {
    if (!(date instanceof Date)) return null;
    return this.formatDate(date);
  }

  initForms() {
    const { from, to } = this.billingService.getDefaultSettlementsDateRange();
    const defaultOption = {
      label: 'Últimos 24 meses',
      value: { startDate: from, endDate: to },
    };
    this.configForm = this.fb.group({
      comparativas: this.fb.array([this.createDateGroup()]),
      supplyType: [null, Validators.required],
      predefineDateOption: [defaultOption],
    });
  }
  ngAfterViewInit() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const data = this.chartSignal();
        this.createSummary(data ?? []);
        if (!data?.length || !this.chartContainer) return;
        requestAnimationFrame(() => {
          this.initChart(data);
        });
      });
    });
  }

  createDateGroup(): FormGroup {
    const hoy = new Date();
    const haceDosAnios = new Date();
    haceDosAnios.setFullYear(hoy.getFullYear() - 2);

    return this.fb.group({
      inicio: [haceDosAnios, Validators.required],
      fin: [hoy, Validators.required],
    });
  }

  resumen = {
    avg: '0,00',
    maxV: '0,00',
    maxLabel: '---',
    minV: '0,00',
    minLabel: '---',
    total: '0,00',
    units: '€',
  };
  onSupplyTypeChange(event: any) {
    const value = event.value;
    this.billingService.filterChartBySupplyType(value);
  }
  switchCalendar() {
    this.clearDateRangeYearWarning();
    // Manual → pull-down: keep last predefined selection and search
    if (this.showCalendar === true) {
      this.showCalendar = false;
      void this.applyConfig();
      return;
    }

    // Pull-down → manual: preload same dates, do not search
    const range = this.configForm.get('predefineDateOption')?.value?.value as
      { startDate?: string; endDate?: string } | undefined;
    if (range?.startDate && range?.endDate) {
      const inicio = this.parseDateString(range.startDate);
      const fin = this.parseDateString(range.endDate);
      if (inicio && fin) {
        this.comparativas.at(0).patchValue({ inicio, fin }, { emitEvent: false });
      }
    }
    this.showCalendar = true;
  }

  private parseDateString(value: string): Date | null {
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  createSummary(data: any) {
    if (!data || data.length === 0) {
      this.resumen = {
        avg: '0,00',
        maxV: '0,00',
        maxLabel: '---',
        minV: '0,00',
        minLabel: '---',
        total: '0,00',
        units: '€',
      };
      return;
    }

    let total = 0;
    let maxItem = data[0];
    let minItem = data[0];

    data.forEach((item: any) => {
      total += item.totalAmount;
      if (item.totalAmount > maxItem.totalAmount) {
        maxItem = item;
      }
      if (item.totalAmount < minItem.totalAmount) {
        minItem = item;
      }
    });

    const avg = total / data.length;

    this.resumen.avg = this.spanishFormat(avg);
    this.resumen.total = this.spanishFormat(total);

    this.resumen.maxV = this.spanishFormat(maxItem.totalAmount);
    this.resumen.maxLabel = this.dateSpanishFormat(maxItem.invoiceDate);

    this.resumen.minV = this.spanishFormat(minItem.totalAmount);
    this.resumen.minLabel = this.dateSpanishFormat(minItem.invoiceDate);
  }

  onDateChange(index: number, field: 'inicio' | 'fin') {
    const group = this.comparativas.at(index);
    let { inicio, fin } = group.value;
    if (!(inicio instanceof Date) || !(fin instanceof Date)) return;

    const MAX_YEARS = 2;
    inicio = this.toDateOnly(inicio);
    fin = this.toDateOnly(fin);
    const maxAllowed = this.toDateOnly(this.maxDate);
    const minAllowed = this.toDateOnly(this.minDate);

    if (field === 'inicio') {
      if (inicio > fin) {
        fin = new Date(inicio);
      }
      const maxFin = new Date(inicio);
      maxFin.setFullYear(inicio.getFullYear() + MAX_YEARS);
      if (fin > maxFin) {
        fin = maxFin;
      }
      if (fin > maxAllowed) {
        fin = new Date(maxAllowed);
      }
      // Evitar reescribir inicio (el datepicker ya lo acaba de setear).
      // Diferir Fin: si se hace en el mismo tick que cambia minDate, PrimeNG deja el input en blanco.
      const finToSet = fin;
      setTimeout(() => {
        group.get('fin')?.setValue(this.toDateOnly(finToSet), { emitEvent: false });
      });
      return;
    }

    if (fin < inicio) {
      inicio = new Date(fin);
    }
    const minInicio = new Date(fin);
    minInicio.setFullYear(fin.getFullYear() - MAX_YEARS);
    if (inicio < minInicio) {
      inicio = minInicio;
    }
    if (inicio < minAllowed) {
      inicio = new Date(minAllowed);
    }
    const inicioToSet = inicio;
    setTimeout(() => {
      group.get('inicio')?.setValue(this.toDateOnly(inicioToSet), { emitEvent: false });
    });
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  onInicioYearChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  onInicioMonthChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  private updateDateRangeWarningFromPicker(
    event: { year?: number; month?: number },
    index: number,
  ): void {
    const fin = this.comparativas.at(index).get('fin')?.value;
    const inicioActual = this.comparativas.at(index).get('inicio')?.value;
    if (!(fin instanceof Date) || event.year == null || event.month == null) {
      this.showDateRangeYearWarning.set(false);
      return;
    }

    const day = inicioActual instanceof Date ? inicioActual.getDate() : 1;
    const lastDayOfMonth = new Date(event.year, event.month, 0).getDate();
    const provisionalInicio = new Date(event.year, event.month - 1, Math.min(day, lastDayOfMonth));

    const maxFin = new Date(provisionalInicio);
    maxFin.setFullYear(provisionalInicio.getFullYear() + 2);
    this.showDateRangeYearWarning.set(fin.getTime() > maxFin.getTime());
  }

  clearDateRangeYearWarning(): void {
    this.showDateRangeYearWarning.set(false);
  }

  addComparativa() {
    this.comparativas.push(this.createDateGroup());
  }

  removeComparativa(index: number) {
    this.comparativas.removeAt(index);
  }

  resetConfig() {
    this.comparativas.clear();
    this.addComparativa();
    this.configForm.reset();
  }

  async applyConfig() {
    let tenantSelected = this.$core.selectedBuilding();

    if (!tenantSelected) {
      tenantSelected = await this.$core.getTenantFromLocal();
    }

    if (!tenantSelected?.tenantId) return;

    const dateRange = this.getSelectedDateRange();
    if (!dateRange) return;

    this.chartLoading.set(true);
    this.$spinner.show();
    try {
      await this.billingService.fetchSettlementsForChart(
        tenantSelected.tenantId,
        dateRange.dateStart,
        dateRange.dateEnd,
        { manageSpinner: false },
      );
      this.syncSupplySelection();
      this.first = 0;
      await this.refresh_data(0, this.rows, 'invoiceDate', 'desc', false);
    } catch (error) {
      console.error('Error al aplicar filtro:', error);
    } finally {
      this.chartLoading.set(false);
      this.$spinner.hide();
    }
  }

  private disposeChart(): void {
    if (this.chartResizeHandler) {
      window.removeEventListener('resize', this.chartResizeHandler);
      this.chartResizeHandler = null;
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  }

  private initChart(data: ISettlementItem[]): void {
    const chartDom = this.chartContainer.nativeElement;
    const ro = new ResizeObserver(() => {
      if (chartDom.clientWidth > 0 && chartDom.clientHeight > 0) {
        this.renderChart(chartDom, data);
        ro.disconnect();
      }
    });

    ro.observe(chartDom);
  }
  renderChart(dom: HTMLElement, data: ISettlementItem[]) {
    this.disposeChart();
    const chartData = data;
    this.chartInstance = echarts.init(dom);
    const myChart = this.chartInstance;
    const categories = chartData.map((item) => {
      const date = new Date(item.invoiceDate);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });
    const narrow = dom.clientWidth < 768;
    const amounts = chartData.map((item) => item.totalAmount);
    // Mismo rango que la consulta (pull-down o calendario), no solo comparativas.
    const dateRange = this.getSelectedDateRange();
    const inicioLabel = dateRange ? this.parseDateString(dateRange.dateStart) : null;
    const finLabel = dateRange ? this.parseDateString(dateRange.dateEnd) : null;
    const periodLabel =
      inicioLabel && finLabel
        ? `desde el ${this.dateSpanishFormat(inicioLabel)} hasta el ${this.dateSpanishFormat(finLabel)}`
        : '';
    const option: echarts.EChartsOption = {
      title: {
        text: `Evolución de liquidaciones de ${this.configForm.controls['supplyType'].value}`,
        left: narrow ? '3%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),
        textStyle: {
          // color: 'rgb(64, 163, 64)',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold',
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',
        feature: {
          saveAsImage: {
            title: 'Descargar',
            type: 'png',
            name: 'Grafico_Liquidaciones',
          },
          magicType: {
            type: ['line', 'bar'],
            title: { line: 'Líneas', bar: 'Barras' },
          },
          restore: { title: 'Reset' },
        },
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 100,
        },
        {
          type: 'slider',
          height: 18,
          left: narrow ? '20%' : '10%',
          right: narrow ? '20%' : '10%',
          handleSize: '120%',
          fillerColor: 'rgba(92, 173, 92, 0.2)',
          handleIcon: 'circle',
          brushSelect: false,
          textStyle: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          handleStyle: {
            color: '#5cad5c',
            shadowBlur: 1,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      ],

      grid: {
        top: '16%',
        bottom: '22%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const index = p.dataIndex;
          const dataItem = chartData[index];
          return `
            <div style="font-weight:600; color:#5cad5c">${this.spanishFormat(dataItem.totalAmount)} €</div>
            <div style="font-size:11px; color:#86868b">${this.dateSpanishFormat(dataItem.initDate)} a ${this.dateSpanishFormat(dataItem.endDate)}</div>
          `;
        },
      },
      xAxis: {
        type: 'category',
        name: 'Fecha Liquidación',
        nameLocation: 'middle',
        nameGap: 40,
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          margin: 15,
        },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => this.spanishFormat(value) + ' €',
        },
      },
      series: [
        {
          data: amounts,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: (params: any) => {
              const index = params.dataIndex;
              const lastIndex = amounts.length - 1;
              if (index === lastIndex) {
                return 'rgb(64, 163, 64)';
              }
              return 'rgba(4, 117, 4, 0.49)';
            },
            borderRadius: [6, 6, 0, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 113, 227, 0.03)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };
    myChart.setOption(option);

    const lineColor = 'rgb(64, 163, 64)';
    myChart.on('magictypechanged', (...args: unknown[]) => {
      const params = args[0] as { currentType?: string };
      if (params.currentType === 'line') {
        myChart.setOption({
          series: [
            {
              itemStyle: { color: lineColor },
              lineStyle: { color: lineColor, width: 2 },
              symbol: 'circle',
              symbolSize: 8,
            },
          ],
        });
        return;
      }
      if (params.currentType === 'bar') {
        myChart.setOption({ series: option.series }, { replaceMerge: ['series'] });
      }
    });

    this.chartResizeHandler = () => {
      if (this.chartInstance && !this.chartInstance.isDisposed()) {
        this.chartInstance.resize();
      }
    };
    window.addEventListener('resize', this.chartResizeHandler);
  }

  dateSpanishFormat(dateTime: string | Date | null | undefined): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  spanishFormat(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0,00';

    const numberValue = typeof value === 'string' ? parseFloat(value) : value;

    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);
  }

  downloadPdf(settlement: any): void {
    if (!settlement.id) return;
    const tenantSelected = this.$core.selectedBuilding();
    if (!tenantSelected?.tenantId) {
      console.error('No se encontró tenantId');
      return;
    }
    this.isDownloading.set(true);
    this.$spinner.show();
    this.billingService.downloadSettlement(tenantSelected.tenantId, settlement.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `Liquidación_${settlement.supplyType} - ${this.dateSpanishFormat(settlement.endDate)}.pdf`;
        link.click();

        window.URL.revokeObjectURL(url);
        this.isDownloading.set(false);
        this.$spinner.hide();
      },
      error: (err) => {
        this.isDownloading.set(false);
        this.$spinner.hide();
        console.error('Error descargando PDF:', err);
        this.$toast.error('Ha ocurrido un error al descargar la liquidación.');
      },
    });
  }
  private getCurrentPage(): number {
    return Math.floor(this.first / this.rows);
  }
  public pageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    const pageToLoad = this.getCurrentPage();
    this.refresh_data(pageToLoad, event.rows);
  }

  private tableDataSnapshot(): string {
    return JSON.stringify({
      rows: this.settlementsSignal(),
      total: this.totalRecords(),
    });
  }

  private async waitForTableUpdate(beforeSnapshot: string): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < 8000) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (this.tableDataSnapshot() !== beforeSnapshot) {
        return;
      }
      if (Date.now() - start > 1500) {
        return;
      }
    }
  }

  async refresh_data(
    page: number,
    size: number,
    sortField?: string,
    sortOrder?: string,
    showSpinner = true,
  ): Promise<void> {
    const tenantSelected = this.$core.selectedBuilding();
    if (!tenantSelected?.tenantId) return;

    const dateRange = this.getSelectedDateRange();
    if (!dateRange) return;

    if (showSpinner) {
      this.$spinner.show();
    }

    const snapshot = this.tableDataSnapshot();
    await this.billingService.fetchSettlementsPaginated(
      tenantSelected.tenantId,
      dateRange.dateStart,
      dateRange.dateEnd,
      page,
      size,
      sortField ?? 'invoiceDate',
      sortOrder ?? 'desc',
      showSpinner,
    );

    await this.waitForTableUpdate(snapshot);
  }

  public loadSettlementsLazy(event: any): void {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) return;

    const dateRange = this.getSelectedDateRange();
    const tablePrefetched = dateRange
      ? this.isTablePrefetched(tenantId, dateRange.dateStart, dateRange.dateEnd)
      : false;

    if (this.initialTableLoad && tablePrefetched) {
      this.initialTableLoad = false;
      this.first = 0;
      this.rows = event.rows ?? this.rows;
      this.tableInitialized.set(true);
      return;
    }

    if (this.initialTableLoad) {
      this.first = 0;
      this.rows = event.rows ?? this.rows;
    } else {
      this.first = event.first ?? 0;
      this.rows = event.rows ?? this.rows;
    }
    const pageToLoad = Math.floor(this.first / this.rows);
    const sortField = (event.sortField as string) ?? 'invoiceDate';
    let sortOrder: string | undefined = undefined;
    if (event.sortOrder === 1) sortOrder = 'asc';
    if (event.sortOrder === -1) sortOrder = 'desc';

    const showSpinner = !this.initialTableLoad;
    this.initialTableLoad = false;
    void this.refresh_data(pageToLoad, this.rows, sortField, sortOrder, showSpinner).finally(() =>
      this.tableInitialized.set(true),
    );
  }
  downloadSelectedSettlements(): void {
    const tenant = this.$core.selectedBuilding();
    if (!tenant?.tenantId || !this.settlementsSelected.length) return;
    const ids = this.settlementsSelected.map((s) => s.id);
    this.isDownloading.set(true);
    this.$spinner.show();
    this.billingService
      .downloadSelectedSettlements(tenant.tenantId, ids)
      .pipe(finalize(() => this.isDownloading.set(false)))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `liquidaciones.zip`;
          link.click();
          this.$spinner.hide();
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Error descargando lote:', err);
          this.$spinner.hide();
          this.$toast.error('Ha ocurrido un error al descargar las liquidaciones.');
        },
      });
  }

  getDateDaysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.formatDate(date);
  }

  getDateMonthsAgo(months: number): string {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return this.formatDate(date);
  }

  getDateYearsAgo(years: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - years);
    return this.formatDate(date);
  }
  private formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  saveConfig() {
    const config = this.configForm.value;
    localStorage.setItem('appConfig', JSON.stringify(config));
  }

  sendByEmail(billing: any) {}

  activar() {
    this.router.navigate(['/recibo-verde']);
  }

  private initNarrowViewportListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.narrowViewportQuery = window.matchMedia('(max-width: 767px)');
    this.isNarrowViewport.set(this.narrowViewportQuery.matches);
    this.narrowViewportListener = (event: MediaQueryListEvent) => {
      this.isNarrowViewport.set(event.matches);
    };
    this.narrowViewportQuery.addEventListener('change', this.narrowViewportListener);
  }
}
